'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

const DARWINBOX_CAREERS_URL =
  'https://wcl.darwinbox.in/ms/candidate/careers___.YXBzMTp3b25kZXJjZW1lbnQ6YzpvOjc1YWZjZDU4NjkzNGFmMTBiYTMyZjNhYzBjZDI4ZDgxOjc6MTZmZDpmN2FjY2FmZjg5YzUzODUwM2QxMWU5MGJkOGUwNjRhZmM5ZDk2OGUxNDIyYjAyZDMwMDZlZDQzNGZiNzJiYjc3OnQ6RjpO';

const MIN_HEIGHT = 640;
const MAX_HEIGHT = 24000;

function isDarwinboxOrigin(origin: string) {
  try {
    const host = new URL(origin).hostname;
    return host === 'darwinbox.in' || host.endsWith('.darwinbox.in');
  } catch {
    return false;
  }
}

const HEIGHT_KEYS = [
  'height',
  'frameHeight',
  'scrollHeight',
  'iframeHeight',
  'documentHeight',
  'h',
] as const;

const NESTED_HEIGHT_KEYS = ['data', 'payload', 'messageValue'] as const;

/** Parse a height from a string message (numeric or JSON-encoded). */
function extractHeightFromString(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  try {
    return extractHeight(JSON.parse(trimmed));
  } catch {
    const asNumber = Number(trimmed);
    return Number.isFinite(asNumber) ? asNumber : null;
  }
}

/** Parse a height from an array message (['setHeight', n] or nested items). */
function extractHeightFromArray(data: unknown[]): number | null {
  // iframe-resizer / Locastic style: ['setHeight', 1234]
  if ((data[0] === 'setHeight' || data[0] === 'resize') && typeof data[1] === 'number') {
    return data[1];
  }
  for (const item of data) {
    const height = extractHeight(item);
    if (height != null) return height;
  }
  return null;
}

/** Parse a height from an object message (known keys, then nested containers). */
function extractHeightFromObject(record: Record<string, unknown>): number | null {
  for (const key of HEIGHT_KEYS) {
    const value = record[key];
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string' && Number.isFinite(Number(value))) return Number(value);
  }

  for (const key of NESTED_HEIGHT_KEYS) {
    if (key in record) return extractHeight(record[key]);
  }

  return null;
}

/** Pull a numeric height from common iframe resize message shapes. */
function extractHeight(data: unknown): number | null {
  if (typeof data === 'number' && Number.isFinite(data)) return data;
  if (typeof data === 'string') return extractHeightFromString(data);
  if (Array.isArray(data)) return extractHeightFromArray(data);
  if (!data || typeof data !== 'object') return null;
  return extractHeightFromObject(data as Record<string, unknown>);
}

export default function CareersDarwinboxEmbed() {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState(MIN_HEIGHT);

  const applyHeight = useCallback((next: number) => {
    const clamped = Math.min(MAX_HEIGHT, Math.max(MIN_HEIGHT, Math.ceil(next)));
    setHeight((prev) => (Math.abs(prev - clamped) < 2 ? prev : clamped));
  }, []);

  const tryMeasureSameOrigin = useCallback(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    try {
      const doc = iframe.contentDocument ?? iframe.contentWindow?.document;
      if (!doc) return;
      const next = Math.max(doc.body?.scrollHeight ?? 0, doc.documentElement?.scrollHeight ?? 0);
      if (next > 0) applyHeight(next);
    } catch {
      // Expected for cross-origin Darwinbox embeds.
    }
  }, [applyHeight]);

  useEffect(() => {
    function onMessage(event: MessageEvent) {
      if (!isDarwinboxOrigin(event.origin)) return;
      const next = extractHeight(event.data);
      if (next != null) applyHeight(next);
    }

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [applyHeight]);

  // Re-try measuring after load while the careers SPA hydrates (same-origin only).
  useEffect(() => {
    const timers = [400, 1200, 2500, 5000].map((ms) => window.setTimeout(tryMeasureSameOrigin, ms));
    return () => timers.forEach((id) => window.clearTimeout(id));
  }, [tryMeasureSameOrigin]);

  return (
    <div className="w-full bg-white">
      <iframe
        ref={iframeRef}
        src={DARWINBOX_CAREERS_URL}
        title="Wonder Cement careers"
        className="w-full border-0"
        style={{ height }}
        loading="lazy"
        referrerPolicy="no-referrer-when-downgrade"
        allow="clipboard-write"
        onLoad={tryMeasureSameOrigin}
      />
    </div>
  );
}
